// <s-logo-cursor data-src="..."> replaces the mouse pointer with a logo image across the page's <main>.
// Matches the Brands I love cursor logo: 96px, pops in (scale + fade) and eases toward the pointer with a slight trail.
// Fine pointers with hover only. Links, buttons and form fields keep their normal cursor so actions stay obvious.
const INTERACTIVE = 'a, button, select, label, summary, input, textarea, [role="button"]';

class LogoCursor extends HTMLElement {
  #abort = new AbortController();
  #rafId = 0;
  #target = { x: 0, y: 0 };
  #current = { x: 0, y: 0 };
  #visible = false;
  #reduce = false;

  connectedCallback() {
    const src = this.dataset.src;
    if (!src || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.#reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.scope = this.closest('main') || document.body;

    this.img = document.createElement('img');
    this.img.src = src;
    this.img.alt = '';
    this.img.setAttribute('aria-hidden', 'true');
    Object.assign(this.img.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      width: '96px',
      height: '96px',
      objectFit: 'contain',
      pointerEvents: 'none',
      zIndex: '2147483647',
      transform: 'translate(-50%, -50%)',
      opacity: '0',
      scale: '0.5',
      transition: this.#reduce ? 'none' : 'opacity 200ms var(--ease-ui, ease-out), scale 200ms var(--ease-ui, ease-out)',
    });
    document.body.append(this.img);

    this.styleEl = document.createElement('style');
    this.styleEl.textContent = `
      .logo-cursor-on, .logo-cursor-on * { cursor: none !important; }
      .logo-cursor-on :is(a, button, label, summary, select, [role="button"]),
      .logo-cursor-on :is(a, button, label, summary, [role="button"]) * { cursor: pointer !important; }
      .logo-cursor-on :is(input:not([type="hidden"]), textarea) { cursor: text !important; }
    `;
    document.head.append(this.styleEl);
    this.scope.classList.add('logo-cursor-on');

    const { signal } = this.#abort;
    this.scope.addEventListener('pointermove', (event) => this.#move(event), { signal });
    this.scope.addEventListener('pointerleave', () => this.#hide(), { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
    cancelAnimationFrame(this.#rafId);
    this.#rafId = 0;
    this.img?.remove();
    this.styleEl?.remove();
    this.scope?.classList.remove('logo-cursor-on');
  }

  #move(event) {
    if (event.pointerType !== 'mouse') return this.#hide();
    this.#target.x = event.clientX;
    this.#target.y = event.clientY;
    const overAction = event.target instanceof Element && event.target.closest(INTERACTIVE);
    if (overAction) return this.#hide();
    if (!this.#visible || this.#reduce) {
      // Jump to the pointer on first show so the logo doesn't slide in from its last spot.
      this.#current.x = this.#target.x;
      this.#current.y = this.#target.y;
      this.#place();
    }
    if (!this.#visible) {
      this.#visible = true;
      this.img.style.opacity = '1';
      this.img.style.scale = '1';
    }
    if (!this.#reduce && !this.#rafId) this.#rafId = requestAnimationFrame(() => this.#tick());
  }

  #hide() {
    this.#visible = false;
    if (!this.img) return;
    this.img.style.opacity = '0';
    this.img.style.scale = '0.5';
  }

  #place() {
    this.img.style.translate = `${this.#current.x}px ${this.#current.y}px`;
  }

  #tick() {
    // Same easing as the Brands I love cursor for a slight, springy trail.
    this.#current.x += (this.#target.x - this.#current.x) * 0.45;
    this.#current.y += (this.#target.y - this.#current.y) * 0.45;
    this.#place();
    const settled = Math.abs(this.#target.x - this.#current.x) < 0.3 && Math.abs(this.#target.y - this.#current.y) < 0.3;
    this.#rafId = settled ? 0 : requestAnimationFrame(() => this.#tick());
  }
}

if (!customElements.get('s-logo-cursor')) customElements.define('s-logo-cursor', LogoCursor);
